<template>
    <div>
        <ds-tooltip position="top" :disabled="showTemplatesPopover">
            <template #reference>
                <DsPopover
                    :is-open="showTemplatesPopover"
                    class="templates-popover"
                    position="top-start"
                    hide-arrow
                    sticky
                    @close="closePopover"
                >
                    <template #reference>
                        <DsIconButton
                            name="templates"
                            data-qa="automation-template-button"
                            @click="handleTemplatesButtonClick"
                        />
                    </template>

                    <div class="templates">
                        <DsListItem border-style="none" :description="defaultHeading()" />
                        <DsListItem
                            v-for="(template, i) in popoverTemplates"
                            :key="i"
                            class="template"
                            border-style="none"
                            :title="template.title || 'New Template'"
                            @click.native="handlePopoverTemplatesInsert(template)"
                        />
                    </div>
                    <div class="manage-templates">
                        <DsTextButton
                            data-qa="manage-templates-button"
                            @click="openTemplatesManager"
                        >
                            {{ "Manage Templates" }}
                        </DsTextButton>
                    </div>
                </DsPopover>
            </template>
            <template #default>
                <div>{{ insertSymbolTooltip }}</div>
            </template>
        </ds-tooltip>

        <portal to="kmnTemplateManager">
            <KeapMarketingSmsTemplateManager
                :is-open="isKeapMarketingNumberTemplateManagerOpen"
                :templates="sortedKeapMarketingNumberTemplates"
                :is-spa-app="isSpaApp"
                :source="source"
                @insert-template="handleTemplatesInsert"
                @load-templates="loadTemplates"
                @close="isKeapMarketingNumberTemplateManagerOpen = false"
            />
        </portal>
    </div>
</template>

<script>
import KeapMarketingSmsTemplateManager from '@/components/automatedSms/KeapMarketingSmsTemplateManager';
import sortBy from 'lodash.sortby';
import axios from 'axios';
import { trackKmnTemplateInsert, trackKmnTemplatesManagerOpened } from '@/components/automatedSms/smsAnalytics';
import {
    KMN_AUTOMATION_TEMPLATE_KINDS, TEMPLATES_BROADCAST_DEFAULTS, TEMPLATES_AUTOMATION_DEFAULTS, KMN_BROADCAST_TEMPLATE_KINDS, MEDIA_TYPES,
} from './template.constants.js';

export default {

    components: {
        KeapMarketingSmsTemplateManager,
    },

    props: {
        source: {
            type: String,
            default: 'automation',
        },
    },

    data() {
        return {
            showTemplatesPopover: false,
            isKeapMarketingNumberTemplateManagerOpen: false,
            keapMarketingNumberTemplates: [],
            isSpaApp: false,
            insertSymbolTooltip: '',
        };
    },

    created() {
        if (window.keapI18nMap !== undefined) {
            this.insertSymbolTooltip = window.keapI18nMap['funnel.campaign.communications.insertsymbol.tooltip'];
        } else {
            this.insertSymbolTooltip = 'Insert template';
        }
    },

    mounted() {
        this.checkApp();
        this.loadTemplates();
    },

    watch: {
        showTemplatesPopover: {
            handler() {
                if (this.showTemplatesPopover) this.loadTemplates();
            },
        },
    },

    computed: {

        sortedKeapMarketingNumberTemplates() {
            const existingTemplateKinds = this.keapMarketingNumberTemplates.reduce((acc, template) => {
                if (!acc.includes(template.templateKind)) {
                    acc.push(template.templateKind);
                }

                return acc;
            }, []);

            const defaultTemplatesList = [...TEMPLATES_AUTOMATION_DEFAULTS.defaultKeapMarketingNumberAutomationTemplates, ...TEMPLATES_BROADCAST_DEFAULTS.defaultKeapMarketingNumberBroadcastTemplates];

            const remainingDefaultTemplates = defaultTemplatesList
                .filter((template) => {
                    return !existingTemplateKinds.includes(template.templateKind);
                });

            return sortBy([...this.keapMarketingNumberTemplates, ...remainingDefaultTemplates], ({ updatedOn }) => {
                return updatedOn ? -new Date(updatedOn) : null;
            });
        },

        automationPopoverTemplates() {
            return this.sortedKeapMarketingNumberTemplates
                .filter((template) => {
                    return KMN_AUTOMATION_TEMPLATE_KINDS.includes(template.templateKind);
                }).slice(0, 5);
        },

        broadcastPopoverTemplates() {
            return this.sortedKeapMarketingNumberTemplates
                .filter((template) => {
                    return KMN_BROADCAST_TEMPLATE_KINDS.includes(template.templateKind);
                }).slice(0, 5);
        },

        popoverTemplates() {
            if (this.source === 'broadcast') {
                return this.broadcastPopoverTemplates;
            }

            return this.automationPopoverTemplates;
        },
    },

    methods: {

        async checkApp() {
            try {
                const { data } = await axios.get('/app/automatedSmsFunnel/isSpaApp');

                if (data) {
                    this.isSpaApp = data;
                }
            } catch (e) {
                this.isSpaApp = false;
            }
        },

        async loadTemplates() {
            const params = new URLSearchParams({ type: MEDIA_TYPES.PLAIN_TEXT });

            await axios.get('/app/templateFunnel/templates', { params })
                .then((response) => {
                    const { data } = response;

                    this.keapMarketingNumberTemplates = data.filter((template) => KMN_AUTOMATION_TEMPLATE_KINDS.includes(template.templateKind)
                            || KMN_BROADCAST_TEMPLATE_KINDS.includes(template.templateKind));
                });
        },

        defaultHeading() {
            return 'Insert Template';
        },
        closePopover() {
            this.showTemplatesPopover = false;
        },

        handleTemplatesButtonClick() {
            this.showTemplatesPopover = !this.showTemplatesPopover;
        },

        openTemplatesManager() {
            this.showTemplatesPopover = false;
            this.isKeapMarketingNumberTemplateManagerOpen = true;

            trackKmnTemplatesManagerOpened(this.isSpaApp, this.source === 'automation' ? 'Automations' : 'Broadcasts');
        },

        handlePopoverTemplatesInsert(template) {
            trackKmnTemplateInsert(template, this.source === 'automation' ? 'AA popover' : 'Broadcasts popover', this.isSpaApp);

            this.handleTemplatesInsert(template);
        },

        handleTemplatesInsert(template) {
            this.$emit('insert-template', template);

            this.closePopover();
        },
    },
};
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
$templates-popover-height: px-to-rem(310px);

.templates-popover {
  --popover-background-color: #{$color-paper};
}

.templates {
  max-height: $templates-popover-height;
  max-width: px-to-rem(300px);
  width: 100%;
  display: inline;
}
.template {
  cursor: pointer;

  &:hover {
    background-color: $color-gray-050;
  }
}

.manage-templates {
  display: flex;
  justify-content: center;
  padding: $spacing-100 0;
  border-top: solid px-to-rem(1px) $color-gray-200;
}
</style>
